import { EffectDef, makeGain } from './types';

/** Phaser: cascaded all-pass filters swept by a single LFO. */
export const phaser: EffectDef = {
  type: 'phaser',
  name: 'Phaser',
  params: [
    { key: 'rate', label: 'Rate', min: 0.05, max: 8, default: 0.4, step: 0.05, unit: 'Hz' },
    { key: 'depth', label: 'Depth', min: 0, max: 1, default: 0.6, step: 0.01 },
    { key: 'wet', label: 'Wet', min: 0, max: 1, default: 0.4, step: 0.01 },
  ],
  build(ctx, p) {
    const input = makeGain(ctx, 1);
    const output = makeGain(ctx, 1);
    const dry = makeGain(ctx, 1 - p.wet);
    const wetIn = makeGain(ctx, 1);
    const wetOut = makeGain(ctx, p.wet);

    // Four all-pass stages spread across two octaves.
    const centers = [800, 1600, 3200, 6400];
    const stages = centers.map((f) => {
      const bp = ctx.createBiquadFilter();
      bp.type = 'allpass';
      bp.frequency.value = f;
      bp.Q.value = 0.5;
      return bp;
    });

    const lfo = ctx.createOscillator();
    lfo.frequency.value = p.rate;
    const lfoGains = stages.map((bp, i) => {
      const g = makeGain(ctx, 0);
      g.gain.value = centers[i] * 0.5 * p.depth; // ±50% swing, stays positive
      lfo.connect(g);
      g.connect(bp.frequency);
      return g;
    });
    lfo.start();

    input.connect(dry);
    dry.connect(output);
    input.connect(wetIn);
    wetIn.connect(stages[0]);
    for (let i = 0; i < stages.length - 1; i++) stages[i].connect(stages[i + 1]);
    stages[stages.length - 1].connect(wetOut);
    wetOut.connect(output);

    const apply = (params: Record<string, number>) => {
      lfo.frequency.value = params.rate;
      for (let i = 0; i < stages.length; i++) {
        lfoGains[i].gain.value = centers[i] * 0.5 * params.depth;
      }
      dry.gain.value = 1 - params.wet;
      wetOut.gain.value = params.wet;
    };
    apply(p);

    return {
      input,
      output,
      update: apply,
      dispose: () => {
        try {
          lfo.stop();
        } catch {
          /* already stopped */
        }
        lfo.disconnect();
        for (const g of lfoGains) g.disconnect();
        for (const bp of stages) bp.disconnect();
        dry.disconnect();
        wetIn.disconnect();
        wetOut.disconnect();
        input.disconnect();
        output.disconnect();
      },
    };
  },
};